import { of } from 'rxjs';

import { map, withLatestFrom, switchMap, catchError, skipWhile, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { CAAction } from '../actions';
import * as selectors from '../reducers/index';
import { MarketManagementCategories as actions } from './market-management-category.actions';
import { CategoryDto, ShopCategoryDto, ShopManagementService } from '@careapp-group/ng-sdk';
import { getFilesUploading } from '../file-upload/file-upload-selectors';

@Injectable()
export class MarketManagementEffects {
  createMarketCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCreateAction),
      switchMap((action) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => uploading),
          take(1),
          withLatestFrom(
            this.store.select(selectors.getFileUploadState),
            this.store.select(selectors.getSelectedId(selectors.TENANTS)),
          ),
          switchMap(([uploading, files, tenantId]) => {
            const featuredImageId = action.payload.featuredImageId
              ? (files[action.payload.featuredImageId]?.response?.id ?? null)
              : null;

            return this.marketManagementApi
              .createMarketCategory({
                createShopCategoryDto: {
                  description: action.payload.description,
                  name: action.payload.name,
                  tenantId,
                  featuredImageId,
                },
              })
              .pipe(
                map((response) => actions.ResponseSuccessCreateAction({ payload: response })),
                catchError((error) => of(actions.ResponseFailCreateAction({ error }))),
              );
          }),
        ),
      ),
    ),
  );

  updateMarketCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateAction),
      switchMap((action) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => uploading),
          take(1),
          withLatestFrom(
            this.store.select(selectors.getFileUploadState),
            this.store.select(selectors.getSelectedId(selectors.TENANTS)),
            this.store.select(selectors.getEntities(selectors.MARKET_MANAGEMENT_CATEGORIES)),
          ),
          switchMap(([uploading, files, tenantId, categoryEntities]) => {
            const existingCategory: ShopCategoryDto = categoryEntities[action.id];
            const existingImageId = existingCategory.featuredImageId;
            const hasNewImage = action.data.featuredImageId != existingImageId;

            const imageId = hasNewImage
              ? (files[action.data.featuredImageId]?.response?.id ?? null)
              : existingImageId;

            return this.marketManagementApi
              .patchMarketCategory({
                id: action.id,
                patchShopCategoryDto: {
                  productIds: [],
                  description: action.data.description,
                  name: action.data.name,
                  featuredImageId: imageId,
                },
              })
              .pipe(
                map((response) => actions.ResponseSuccessUpdateAction({ payload: response })),
                catchError((error) =>
                  of(actions.ResponseFailUpdateAction({ id: action.id, error })),
                ),
              );
          }),
        ),
      ),
    ),
  );

  getMarketCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestRetrieveAllAction),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.TENANTS))),
      switchMap(([action, tenantId]) => {
        return this.marketManagementApi.listMarketCategories({ tenantId }).pipe(
          map((response) => actions.ResponseSuccessRetrieveAllAction({ payload: response })),
          catchError((error) => of(actions.ResponseFailRetrieveAllAction({ error }))),
        );
      }),
    ),
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private marketManagementApi: ShopManagementService,
  ) {}
}
